import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { useRemoveMember, useResendInvite } from '@/hooks/api/useMembers';
import type { OrganizationMember } from '@kirin/api-client/contracts/organization-members';

/**
 * Member actions component
 * Handles resending a pending invite, removing active members, and deleting invites.
 * The remove-member endpoint automatically handles deleting invited users vs removing active members.
 */
export const MemberActions = ({
  member,
  canRemove,
  organizationId,
}: {
  member: OrganizationMember;
  canRemove: boolean;
  organizationId: string;
}) => {
  const { mutateAsync: removeMember, isPending: isRemoving } =
    useRemoveMember();
  const { mutateAsync: resendInvite, isPending: isResending } =
    useResendInvite();
  const { toast } = useToast();

  // Drive invite-specific copy off the invite-row fact, not `authStatus`
  // (KIR-4040): a member added silently (MDM / the org handshake path) can carry
  // Descope status `invited` while being a real membership, and must not be
  // labelled "Delete this invitation?" or offered a Resend that would 404.
  const hasPendingInvite = member.hasPendingInvite === true;

  // messages based on invite vs member status
  const messages = {
    confirm: hasPendingInvite
      ? 'Are you sure you want to delete this invitation?'
      : 'Are you sure you want to remove this member from the organization?',
    success: hasPendingInvite
      ? 'Invitation deleted successfully'
      : 'Member removed successfully',
    error: hasPendingInvite
      ? 'Failed to delete invitation'
      : 'Failed to remove member',
    errorLog: hasPendingInvite
      ? 'Failed to delete invite'
      : 'Failed to remove member',
    buttonLabel: hasPendingInvite ? 'Delete' : 'Remove',
    buttonLabelLoading: hasPendingInvite ? 'Deleting...' : 'Removing...',
  };

  const handleRemove = async () => {
    if (confirm(messages.confirm)) {
      try {
        await removeMember({
          organizationId,
          userId: member.id,
        });
        toast({
          title: 'Success',
          description: messages.success,
          variant: 'success',
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : messages.error;
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
        console.error(`${messages.errorLog}:`, error);
      }
    }
  };

  // Resend rotates the invite token, so any link the invitee may be mid-click on
  // stops working — the success toast says so (KIR-4040). No confirm(): resending
  // is additive, and the destructive confirm stays on Delete only.
  const handleResend = async () => {
    try {
      await resendInvite({
        organizationId,
        userId: member.id,
      });
      toast({
        title: 'Success',
        description:
          'Invitation resent — the previous invite link is no longer valid',
        variant: 'success',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to resend invitation';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Failed to resend invite:', error);
    }
  };

  return (
    <>
      {hasPendingInvite && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleResend}
          disabled={!canRemove || isResending}
        >
          {isResending ? 'Resending...' : 'Resend'}
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={handleRemove}
        disabled={!canRemove || isRemoving}
      >
        {isRemoving ? messages.buttonLabelLoading : messages.buttonLabel}
      </Button>
    </>
  );
};
